/* eslint-disable @typescript-eslint/no-explicit-any */
import { ROUTES } from "domain/configuration";
import {
  createPlanChooserSignUpAction,
  getFlickSignUpAction,
} from "domain/core/actions";
import {
  clearState,
  setCreationSourceIdentifier,
  setCurrentFormStep,
  setFlickUserId,
  setPartialSignUpDialogState,
} from "domain/core/reducers";
import { AddressType } from "domain/core/types";
import { compareSanitizedStrings, formatAddress } from "lib/util";
import qs from "qs";
import React, { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "store/storeHooks";

const compareAddresses = (
  parsedQueryAddress: qs.ParsedQs,
  address: AddressType | undefined,
): boolean => {
  if (!address || !parsedQueryAddress) return false;
  return (
    compareSanitizedStrings(
      parsedQueryAddress.number as string,
      address.number,
    ) &&
    compareSanitizedStrings(
      parsedQueryAddress.postcode as string,
      address?.postcode,
    ) &&
    compareSanitizedStrings(parsedQueryAddress.street as string, address.street)
  );
};

export const PartialSignUpDialog = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { selectedAddress: address, partialSignUpDialogOpen } = useAppSelector(
    (store) => store.coreData,
  );

  const { search, pathname } = useLocation();

  const locallyStoredFormPath =
    localStorage.getItem("currentFormStep") || ROUTES.root;

  const localSignUpUUID = localStorage.getItem("signUpId");

  const handleContinueToJoinClick = useCallback(() => {
    dispatch(setPartialSignUpDialogState(false));
  }, []);

  const clearLocalStorage = () => {
    dispatch(clearState());
    dispatch(setCurrentFormStep(ROUTES.root));
    dispatch(setPartialSignUpDialogState(false));
    navigate(ROUTES.root);
  };

  useEffect(() => {
    const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true });
    const querySignUpUUID = parsedQuery.signUpUUID?.toString();
    // give precedence to query ids as they're more likely to be coming via specific links
    const signUpId = querySignUpUUID || localSignUpUUID;
    const redirectedFromCreditCard =
      parsedQuery.userid !== undefined && parsedQuery.result !== undefined;

    if (signUpId) {
      dispatch(setFlickUserId(signUpId));
      dispatch(getFlickSignUpAction.request(signUpId));
    }

    if (!redirectedFromCreditCard) {
      displayPartialSignUp(parsedQuery);
    }

    if (parsedQuery.creation_source === "cxagent") {
      dispatch(setCreationSourceIdentifier());
    }
  }, []);

  const displayPartialSignUp = useCallback((params: any) => {
    const initialFormStep = locallyStoredFormPath === ROUTES.root;
    const addressInfoInParams =
      params.number !== undefined || params.street !== undefined;
    const storedSignUpAddressMatchesParams = compareAddresses(params, address);
    const getSignUpViaUrl = !initialFormStep && !addressInfoInParams;

    // dont use params when matching steps for routing as they cause page refresh dialogues.
    const storedFormStepWithoutParams = locallyStoredFormPath?.split("?")[0];
    // Compare the current form step eg `/join` with the current path without a trailing slash
    const notOnStoredFormStep =
      storedFormStepWithoutParams !== pathname.replace(/\/$/, "");

    if (!addressInfoInParams && notOnStoredFormStep) {
      navigate(locallyStoredFormPath);
    }

    if (
      (addressInfoInParams && storedSignUpAddressMatchesParams) ||
      getSignUpViaUrl
    ) {
      dispatch(setPartialSignUpDialogState(true));
    }
    // if customer has arrived from outside of the join form with address details
    // (e.g from main flick site or a 3rd party referrer), reset existing state so
    // that they can fill out the form without any old details.
    if (addressInfoInParams && !storedSignUpAddressMatchesParams) {
      dispatch(clearState());
      populatePlanChooserAddress(params);
    }
  }, []);

  const populatePlanChooserAddress = useCallback(
    (params: any) => {
      // Undefined values passed via params end up as the string "undefined".
      // Because strings are truthy values they get passed to the sign up api and cause
      // issues with validations for things like postcode length, so we need to strip
      // them out of the query string object before we make an addree variable.
      Object.entries(params).forEach(([key, value]) => {
        // the query string object has a few potential types even though
        // we always receive these as strings so we need to explicitly check
        // for a string value here before typescript will stop screaming at us.
        const stringValue =
          typeof value == "string" ? value?.toLowerCase() : value;
        if (stringValue === "undefined") {
          delete params[key];
        }
      });
      // Dont re-route the user based on any partially completed sign ups
      // if they've come from the estimate action on the main site. Instead persist
      // the data that came through params and send them to the details step.
      const currentAddress: AddressType = {
        number: params.number as string,
        street: params.street as string,
        suburb: params?.suburb as string,
        city: params?.city as string,
        postcode: params?.postcode as string,
        unit: params?.unit as string,
        region: params?.region as string,
        fullAddress: "",
      };
      dispatch(createPlanChooserSignUpAction.request(currentAddress));
      const formStep = `/join/details?product=${params.product}`;
      dispatch(setCurrentFormStep(formStep));
      navigate(formStep);
    },
    [address],
  );

  if (!partialSignUpDialogOpen) return null;

  return (
    <div id="accessible-dialog" className="dialog_container">
      <div className="dialog_overlay" data-a11y-dialog-hide></div>
      <div role="dialog" className="dialog" aria-labelledby="dialog-title">
        <button
          type="button"
          data-a11y-dialog-hide
          aria-label="Close this dialog window"
          className="dialog_close"
          onClick={handleContinueToJoinClick}
        ></button>
        <div className="dialog_contents text_block--center">
          <h1>Welcome Back!</h1>
          <p>Would you like to continue your latest application for:</p>
          <h2 className="h5">{formatAddress(address)}</h2>
          <div className="form_row">
            <button
              type="button"
              className="button button_primary partial-sign-up-dialog_button"
              id="submit-re-estimate"
              onClick={handleContinueToJoinClick}
            >
              Continue Sign Up
            </button>
          </div>
          <div className="form_row">
            <button
              type="button"
              className="button button_secondary partial-sign-up-dialog_button"
              id="button-close"
              onClick={clearLocalStorage}
            >
              Start Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
